import { Space, Table, Tag, Tooltip, Typography, theme } from 'antd';
import { useMemo } from 'react';
import { budgetOverlayBadges, stageMeta, stageOf } from 'assets/data/estadosPresupuesto';
import BudgetRowActions, { ACTIONS_WIDTH } from '../BudgetRowActions';

const { useToken } = theme;
const { Text } = Typography;

// El estado del documento llega como "Aprobado" para todo presupuesto aprobado, aunque
// ya tenga ODT en curso o finalizada; la etapa del ciclo dice dónde va realmente.
// Una etapa desconocida cae al estado que trae la fila.
const estadoDe = (record) => {
  const meta = stageMeta[stageOf(record)];
  return {
    description: meta?.description ?? record.statusDescription,
    color: meta?.color ?? record.statusColor,
  };
};

// Segunda línea del cliente: "dirección · comuna", omitiendo lo que falte.
const ubicacionDe = (record) => [record.address, record.commune].filter(Boolean).join(' · ');

function BudgetTable({
  rows,
  total,
  loading,
  page,
  pageSize,
  onPageChange,
  onEdit,
  onApprove,
  onOpenOdtForm,
  onOpenOdtStatus,
  onDownloadOdt,
  onOpenGantt,
  refetch,
}) {
  const { token } = useToken();

  const columns = useMemo(
    () => [
      {
        title: 'Estado',
        key: 'status',
        width: 170,
        render: (record) => {
          const badges = budgetOverlayBadges(record);
          const estado = estadoDe(record);
          return (
            <Space direction="vertical" size={4} align="start">
              <Tag
                color={estado.color}
                style={{
                  fontWeight: 500,
                  paddingInline: token.paddingXS,
                  paddingBlock: 2,
                  borderRadius: token.borderRadiusSM,
                  marginInlineEnd: 0,
                }}
              >
                {estado.description}
              </Tag>
              {badges.length > 0 ? (
                <Space size={4} wrap>
                  {badges.map((b) => (
                    <Tag key={b.key} color={b.color} style={{ marginInlineEnd: 0 }}>
                      {b.text}
                    </Tag>
                  ))}
                </Space>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: 'N°',
        dataIndex: 'idBudget',
        key: 'idBudget',
        width: 80,
        sorter: (a, b) => a.idBudget - b.idBudget,
        render: (id) => (
          <Text strong style={{ color: token.colorPrimary }}>
            #{id}
          </Text>
        ),
      },
      {
        title: 'Cliente',
        key: 'name',
        width: 300,
        render: (record) => {
          const ubicacion = ubicacionDe(record);
          return (
            <Tooltip title={record.email || null} placement="topLeft">
              <div style={{ minWidth: 0 }}>
                <Text strong ellipsis style={{ display: 'block', textTransform: 'capitalize' }}>
                  {record.name}
                </Text>
                {ubicacion ? (
                  <Text
                    type="secondary"
                    ellipsis
                    style={{ display: 'block', fontSize: token.fontSizeSM }}
                  >
                    {ubicacion}
                  </Text>
                ) : null}
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: 'Descripción',
        dataIndex: 'reason',
        key: 'reason',
        ellipsis: { showTitle: false },
        render: (reason) => (
          <Text type={reason ? undefined : 'secondary'} ellipsis={{ tooltip: reason }}>
            {reason || 'Sin descripción'}
          </Text>
        ),
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: ACTIONS_WIDTH + 32,
        align: 'left',
        fixed: 'right',
        render: (record) => (
          <BudgetRowActions
            record={record}
            onEdit={onEdit}
            onApprove={onApprove}
            onOpenOdtForm={onOpenOdtForm}
            onOpenOdtStatus={onOpenOdtStatus}
            onDownloadOdt={onDownloadOdt}
            onOpenGantt={onOpenGantt}
            refetch={refetch}
          />
        ),
      },
    ],
    [token, onEdit, onApprove, onOpenOdtForm, onOpenOdtStatus, onDownloadOdt, onOpenGantt, refetch]
  );

  return (
    <Table
      rowKey="idBudget"
      dataSource={rows}
      columns={columns}
      loading={loading}
      size="middle"
      scroll={{ x: 900 }}
      pagination={{
        current: page,
        pageSize,
        total,
        onChange: onPageChange,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '25', '50', '100'],
        showTotal: (t, range) => `${range[0]}-${range[1]} de ${t} presupuestos`,
      }}
    />
  );
}

export default BudgetTable;
